/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 18/05/22
 */

import { LabeledSelect } from '@/components/FormInputField';
import { FormControlWrapperWithLabel } from '@/components/FormInputField/FormControlWrapper';
import UserSearch from '@/components/pages/Admin/Supervisor/UserSearch';
import UserDetails from '@/components/pages/Profile/UserDetails';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { User } from '@/wpt-lib/interfaces/User';
import { useAsyncLayoutEffect } from '@/utils/hooks/useAsyncEffect';
import { useGetRequest } from '@/utils/useStatefulRequests';
import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import React, { useState } from 'react';
import { formatName } from '@/wpt-lib/utils/formatName';

const UserAssignmentPicker = ({
    currentAssignment,
    onSelectAssignment,
    loading,
}: {
    currentAssignment: Assignment;
    onSelectAssignment: (assignment: Assignment, user: User) => Promise<boolean>;
    loading?: boolean;
}) => {
    const [selectedUser, setSelectedUser] = useState<User>();
    const [selectedAssignment, setSelectedAssignment] = useState<Assignment>();

    const onChooseUser = (user: User) => {
        setSelectedUser(user);
    };

    const {
        error: fetchAssignmentsError,
        loading: fetchingAssignments,
        responseData: userAssignments,
        makeRequest: fetchAssignments,
    } = useGetRequest<Assignment[]>();

    useAsyncLayoutEffect(async () => {
        if (selectedUser) {
            try {
                await fetchAssignments(`users/${selectedUser.id}/assignments`, {
                    params: {
                        assignment: currentAssignment?.id,
                    },
                });
                setSelectedAssignment(undefined);
            } catch (e) {
                // handled by hook
            }
        }
    }, [selectedUser, currentAssignment]);

    useAsyncLayoutEffect(async () => {
        if (userAssignments?.length && !selectedAssignment) {
            setSelectedAssignment(userAssignments[0]);
        }
    }, [userAssignments, selectedAssignment]);

    const handleAdd = async () => {
        const success = await onSelectAssignment(selectedAssignment as Assignment, selectedUser as User);
        if (success) {
            setSelectedUser(undefined);
            setSelectedAssignment(undefined);
        }
    };

    return (
        <VStack w="full" alignItems="flex-start">
            <FormControlWrapperWithLabel label="Select User" labelProps={{ fontWeight: 'bold' }}>
                <UserSearch onSelect={onChooseUser} />
            </FormControlWrapperWithLabel>
            {selectedUser && (
                <>
                    <Text fontWeight="bold">Selected User:</Text>
                    <UserDetails name={formatName(selectedUser)} id={selectedUser.staffId} />
                    <StateDisplay
                        loading={fetchingAssignments}
                        error={!!fetchAssignmentsError}
                        errorRender={() => <SimpleAlert variant="subtle">Could not load User Assignments</SimpleAlert>}
                        data={userAssignments}
                        render={(loadedAssignments) => (
                            <HStack w="full">
                                <LabeledSelect
                                    label="Select User Assignment"
                                    labelProps={{
                                        fontWeight: 'bold',
                                    }}
                                    onChange={(ev) =>
                                        setSelectedAssignment(
                                            loadedAssignments.find((a) => a.id === parseInt(ev.target.value)),
                                        )
                                    }
                                >
                                    {loadedAssignments
                                        .sort((a, b) => a.assignmentNumber.localeCompare(b.assignmentNumber))
                                        .map((a) => (
                                            <option
                                                key={a.id}
                                                value={a.id}
                                                style={{
                                                    background: 'unset',
                                                }}
                                            >
                                                {a.assignmentNumber} - {a.name}
                                            </option>
                                        ))}
                                </LabeledSelect>
                                <Button
                                    colorScheme="blue"
                                    isDisabled={!selectedAssignment}
                                    isLoading={loading}
                                    onClick={handleAdd}
                                >
                                    Add
                                </Button>
                            </HStack>
                        )}
                    />
                </>
            )}
        </VStack>
    );
};

export default UserAssignmentPicker;
